import { FC, ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { PunchupTooltip, PunchupTooltipProps } from '../../../feedback/PunchupTooltip';
import styles from './PunchupFieldLabel.module.css';

/**
 * "Explain this field" help content. A bare string is the one-paragraph form; the object form adds an
 * `secondaryText` example for a control whose mechanics need one (e.g. what a date-shifting toggle does).
 */
export type PunchupFieldHelp = string | Pick<PunchupTooltipProps, 'mainText' | 'secondaryText'>;

export interface PunchupFieldHelpIconProps {
	/** The help content to reveal on hover/focus. */
	help: PunchupFieldHelp;
	/** Accessible name for the help trigger. */
	ariaLabel: string;
}

/**
 * The help affordance itself — a muted 12px `HelpCircle` that opens a `PunchupTooltip` on hover/focus.
 * Rendered beside a label as a SIBLING, never inside the `<label>` (which would forward clicks to the
 * control). Wired to `PunchupTooltip` DIRECTLY, not `PunchupHelpButton` (a card-corner absolute anchor).
 */
export const PunchupFieldHelpIcon: FC<PunchupFieldHelpIconProps> = ({ help, ariaLabel }) => (
	<PunchupTooltip {...(typeof help === 'string' ? { mainText: help } : help)}>
		<span className={styles.help} tabIndex={0} role="button" aria-label={ariaLabel}>
			{/* 12px to match the analytics KPI help icon (PunchupHelpButton) — same muted colour + size
			    so the help affordance reads identically everywhere. */}
			<HelpCircle size={12} aria-hidden />
		</span>
	</PunchupTooltip>
);

export interface PunchupFieldLabelProps {
	/** Label text / content. */
	label: ReactNode;
	/** Associates a `<label>` with its control by id. Ignored when `as="span"`. */
	htmlFor?: string;
	/** Shows the required marker via the `punchupFormLabel` `data-required` hook. */
	required?: boolean;
	/**
	 * Optional "explain this field" help. Renders a muted 12px help icon beside the label that opens a
	 * `PunchupTooltip` on hover/focus. Use for a field's STATIC purpose — not for contextual state
	 * (`PunchupNumericInput.hint`), toggle `info`, or radio-card `description`, which own those.
	 */
	help?: PunchupFieldHelp;
	/** `'label'` (default) renders `<label htmlFor>`; `'span'` for group labels that have no single control. */
	as?: 'label' | 'span';
	/** Accessible name for the help trigger. Defaults from `label` when it is a string. */
	helpAriaLabel?: string;
}

/**
 * PunchupFieldLabel
 *
 * The single source for a Punchup form-field label. Owns the canonical `punchupFormLabel` markup + the
 * `data-required` asterisk hook, and adds an optional inline help affordance (`PunchupFieldHelpIcon`).
 * With no `help` it renders the bare label, byte-identical to the inline `<label className="punchupFormLabel">`
 * the field primitives used before adopting this.
 */
export const PunchupFieldLabel: FC<PunchupFieldLabelProps> = ({
	label,
	htmlFor,
	required,
	help,
	as = 'label',
	helpAriaLabel,
}) => {
	const LabelTag = as;
	const labelNode = (
		<LabelTag
			className="punchupFormLabel"
			htmlFor={as === 'label' ? htmlFor : undefined}
			data-required={required || undefined}
		>
			{label}
		</LabelTag>
	);

	if (!help) return labelNode;

	const ariaLabel = helpAriaLabel ?? (typeof label === 'string' ? `${label} — more information` : 'More information');

	return (
		<div className={styles.labelRow}>
			{labelNode}
			<PunchupFieldHelpIcon help={help} ariaLabel={ariaLabel} />
		</div>
	);
};
